import { Component, computed, inject, OnInit, signal, viewChild } from '@angular/core';
import { sortBy } from 'lodash';
import { concat, Subject } from 'rxjs';
import { debounceTime, filter, toArray } from 'rxjs/operators';
import { NotifyService } from '../../service/notify.service';
import { UserAdminService } from '../../service/user-admin.service';
import { UserLoginService } from '../../service/user-login.service';
import { Branch, BranchRole, SelectableBranchRole, UserProfile } from '../../types';
import { NgSelectModule } from '@ng-select/ng-select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { UiModule } from '../../components/ui.module';
import { AdminUserManagementCreateUserComponent } from '../admin-user-management-create-user/admin-user-management-create-user.component';
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { CdkVirtualScrollViewport, ScrollDispatcher, ScrollingModule } from '@angular/cdk/scrolling';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { possibleBranchRoles } from '../../constants';
import { PageSelectBranchComponent } from '../../secure/components/page-select-branch/page-select-branch.component';

type SelectableBranch = Branch & {
  _selectableRoles: SelectableBranchRole[];
};

@Component({
  selector: 'app-admin-user-management-userbranches',
  templateUrl: './admin-user-management-userbranches.component.html',
  styleUrl: './admin-user-management-userbranches.component.scss',
  standalone: true,
  imports: [
    UiModule,
    SweetAlert2Module,
    FormsModule,
    ReactiveFormsModule,
    NgSelectModule,
    AdminUserManagementCreateUserComponent,
    ScrollingModule,
    PageSelectBranchComponent,
  ],
})
export class AdminUserManagementUserBranchesComponent implements OnInit {
  private adminService = inject(UserAdminService);
  private notifications = inject(NotifyService);
  public userLogin = inject(UserLoginService);

  private scrollDispatcher = inject(ScrollDispatcher);

  public selectedUser = signal<UserProfile | undefined>(undefined);

  public branchFilter = signal<string>('');
  public userFilter = signal<string>('');

  public hasLoadedUsers = signal<boolean>(false);
  public hasLoadedBranches = signal<boolean>(false);
  public hasLoadedPage = computed(() => this.hasLoadedUsers() && this.hasLoadedBranches());

  public allBranches = signal<SelectableBranch[]>([]);
  public users = signal<UserProfile[]>([]);

  public modalBranch = signal<SelectableBranch | undefined>(undefined);
  public modalRoles = signal<BranchRole[]>([]);

  public readonly possibleRoles = possibleBranchRoles;

  public visibleUserBranches = computed(() =>
    sortBy(
      this.allBranches()
        .filter((b) => this.selectedUser()?.roles.branch[b.id])
        .filter((b) => b.name.toLowerCase().includes(this.branchFilter().toLowerCase())),
      (b) => b.name.toLowerCase(),
    ),
  );
  public visibleUsers = computed(() => this.users());

  public canSubmitModal = computed(() => this.modalBranch() && this.modalRoles().length > 0);

  public currentUserPage = signal<number>(1);
  public totalUsers = signal<number>(0);
  public hasMoreUsers = signal<boolean>(false);
  public isLoadingMoreUsers = signal<boolean>(false);

  public virtualScroll = viewChild<CdkVirtualScrollViewport>('userScroll');
  public searchInput = new Subject<string>();

  constructor() {
    this.searchInput
      .pipe(takeUntilDestroyed())
      .pipe(debounceTime(300))
      .subscribe((searchTerm: string) => {
        this.userFilter.set(searchTerm);
        this.loadMoreUsers(1);
      });

    this.scrollDispatcher
      .scrolled()
      .pipe(takeUntilDestroyed())
      .pipe(debounceTime(100))
      .pipe(filter(() => this.virtualScroll().getRenderedRange().end === this.virtualScroll().getDataLength()))
      .subscribe(() => {
        if (this.hasMoreUsers() && !this.isLoadingMoreUsers()) {
          this.loadMoreUsers(this.currentUserPage() + 1);
        }
      });
  }

  ngOnInit() {
    this.loadPage();
  }

  loadPage(): void {
    this.loadBranches();
    this.loadUsers();
  }

  public loadBranches() {
    this.hasLoadedBranches.set(false);

    this.adminService.getMyBranches().subscribe((branches) => {
      const pageBranches = branches as SelectableBranch[];

      pageBranches.forEach((branch) => {
        branch._selectableRoles = this.selectableRolesForBranch(branch);
      });

      this.allBranches.set(pageBranches);
      this.hasLoadedBranches.set(true);
    });
  }

  public loadUsers() {
    this.hasLoadedUsers.set(false);
    this.users.set([]);

    this.loadMoreUsers(1);
  }

  public loadMoreUsers(page: number) {
    this.isLoadingMoreUsers.set(true);

    this.adminService
      .getMyUsersForBranches({
        page,
        items_per_page: 200,
        search: this.userFilter(),
      })
      .subscribe((usersData) => {
        this.hasMoreUsers.set(usersData.has_more);
        this.totalUsers.set(usersData.total_items);
        this.currentUserPage.set(usersData.page_number);

        if (usersData.page_number === 1) {
          this.users.set(usersData.items);
        } else {
          this.users.update((users) => [...users, ...usersData.items]);
        }

        this.isLoadingMoreUsers.set(false);
        this.hasLoadedUsers.set(true);
      });
  }

  selectUser(user: UserProfile): void {
    this.branchFilter.set('');
    this.selectedUser.set(user);
  }

  private selectableRolesForBranch(branch: Branch) {
    return this.possibleRoles.map((role) => ({
      ...role,
      disabled: !this.userLogin.canGrantBranchPermission(role.id, branch.id.toString()),
    }));
  }

  addUserRole(user: UserProfile, role: SelectableBranchRole, branch: Branch): void {
    this.adminService.grantBranchRole(user.user_id, branch.id.toString(), role.id).subscribe(() => {
      this.notifications.notify(
        `Successfully granted permission "${role.name}" for user "${user.name || user.email}" in branch "${branch.name}"`,
      );
    });
  }

  removeUserRole(user: UserProfile, role: SelectableBranchRole, branch: Branch): void {
    this.adminService.revokeBranchRole(user.user_id, branch.id.toString(), role.id).subscribe(() => {
      this.notifications.notify(
        `Successfully revoked permission "${role.name}" for user "${user.name || user.email}" in branch "${branch.name}"`,
      );

      if (user.roles.branch[branch.id].length === 0) {
        this.users.update((users) => users.filter((u) => u.user_id !== user.user_id));
      }
    });
  }

  addBranchToUser(): void {
    const chosenUser = structuredClone(this.selectedUser());

    chosenUser.roles.branch[this.modalBranch().id] = this.modalRoles();

    concat(
      ...this.modalRoles().map((role) => this.adminService.grantBranchRole(chosenUser.user_id, this.modalBranch().id.toString(), role)),
    )
      .pipe(toArray())
      .subscribe(() => {
        this.notifications.notify(
          `Successfully added user "${chosenUser.name || chosenUser.email}" to branch "${this.modalBranch().name}"`,
        );
        this.branchFilter.set('');
        this.modalRoles.set([]);
        this.modalBranch.set(undefined);
        this.selectedUser.set(chosenUser);
      });
  }

  removeUserPermissions(user: UserProfile, branch: Branch): void {
    concat(
      ...user.roles.branch[branch.id.toString()].map((role) =>
        this.adminService.revokeBranchRole(user.user_id, branch.id.toString(), role),
      ),
    )
      .pipe(toArray())
      .subscribe(() => {
        this.notifications.notify(`Successfully removed user "${user.name || user.email}" from branch "${branch.name}"`);
        const userCopy = structuredClone(this.selectedUser());
        delete userCopy.roles.branch[branch.id.toString()];
        this.selectedUser.set(userCopy);
      });
  }

  addUserToList(user: UserProfile): void {
    this.users.update((users) => {
      return [...users, user];
    });

    this.userFilter.set(user.name);
    this.selectedUser.set(user);
  }
}
